import {SubmissionDto, SubmissionFilterDto} from "../../client/submission/SubmissionApiClient";
import {useCallback, useMemo} from "react";
import {GridColDef} from "@mui/x-data-grid-pro";
import {SubmissionTranslKey} from "./SubmissionTranslKey";
import {DateTimeFormatter} from "../../common/DateTimeFormatter";
import {formatUserLastNameFirst} from "../user/UserFormatter";
import {useTranslation} from "react-i18next";
import {SubmissionStateTypeFormatter} from "./SubmissionStateTypeFormatter";
import {useCatalog} from "../../common/catalog/UseCatalog";
import {CatalogType} from "../../client/catalog/CatalogType";
import {TFunction} from "i18next";
import {GridComparator} from "../../common/GridComparator";
import PageableGrid, {PageLoadSize} from "../../common/pagination/PageableGrid";
import {FilterSearchType} from "../person/PersonSearchPanel";

export type SubmissionCustomColumns = '#documentMetadata.type';
export type SubmissionColumn = (keyof SubmissionDto | SubmissionCustomColumns);

function filterGridColumns(
    allowedColumns: SubmissionColumn[] | undefined,
    gridColumns: GridColDef[]
): GridColDef[] {
    if (!allowedColumns) {
        return gridColumns;
    }
    return gridColumns.filter(column =>
        allowedColumns.includes(column.field as SubmissionColumn)
    );
}

function buildSubmissionGridColumns(allowedColumns: SubmissionColumn[] | undefined, t: TFunction) {
    const submissionGridColumns: GridColDef[] = [
        {
            field: "submissionNumber",
            headerName: t(SubmissionTranslKey.DOCUMENT_NUMBER),
            width: 150
        },
        {
            field: "#documentMetadata.type",
            headerName: t(SubmissionTranslKey.DOCUMENT_TYPE),
            width: 230,
            valueGetter: params => params.row.documentMetadata.type.code,
            valueFormatter: params => t(params.value, {ns: CatalogType.DOCUMENT_TYPE})
        },
        {
            field: "currentSubmissionState",
            headerName: t(SubmissionTranslKey.CURRENT_STATE),
            width: 150,
            valueFormatter: params => SubmissionStateTypeFormatter.formatSubmissionStateType(params.value?.type, t),
            sortComparator: GridComparator.formattedStringComparator(value => SubmissionStateTypeFormatter.formatSubmissionStateType(value?.type, t))
        },
        {
            field: "submittedAt",
            type: "dateTime",
            headerName: t(SubmissionTranslKey.SUBMITTED_AT),
            width: 200,
            valueGetter: params => params.value ? new Date(params.value) : undefined,
            valueFormatter: params => DateTimeFormatter.formatDateTime(params.value)
        },
        {
            field: "submittedBy",
            headerName: t(SubmissionTranslKey.SUBMITTED_BY),
            width: 200,
            valueFormatter: params => formatUserLastNameFirst(params.value),
            sortComparator: GridComparator.formattedStringComparator(value => formatUserLastNameFirst(value))
        },
    ];

    return filterGridColumns(allowedColumns, submissionGridColumns);
}

export interface SubmissionGridProps {
    rowsData: SubmissionDto[];
    totalCount: number;
    selectedSubmissionId?: number;

    submissionsGridDefColumns?: SubmissionColumn[];

    nextPageToken?: string | null;
    submissionFilter: SubmissionFilterDto;
    fetchSubmissions: (filter: SubmissionFilterDto, searchType: FilterSearchType) => void;

    onSelectSubmission?: (submission: SubmissionDto) => void;
    onSubmissionDoubleClick?: (submissionId: number) => void;
}

export default function SubmissionGrid(props: Readonly<SubmissionGridProps>) {
    const {rowsData, totalCount, selectedSubmissionId, submissionsGridDefColumns, nextPageToken, submissionFilter, fetchSubmissions, onSelectSubmission, onSubmissionDoubleClick} = props;

    const {t} = useTranslation();
    const {isLoading: documentTypeCatalogLoading} = useCatalog(CatalogType.DOCUMENT_TYPE);

    const columns: GridColDef<SubmissionDto>[] = useMemo(() => buildSubmissionGridColumns(submissionsGridDefColumns, t),
        [t, submissionsGridDefColumns]);

    const handleFetchMore = useCallback(() => {
        if (!nextPageToken) {
            return;
        }

        const nextPageFilter: SubmissionFilterDto = {
            ...submissionFilter,
            paging: {
                maxPageSize: PageLoadSize.NEXT_PAGE_SIZE,
                pageToken: nextPageToken,
            },
        };

        fetchSubmissions(nextPageFilter, FilterSearchType.NEXT_PAGE_SEARCH);
    }, [nextPageToken, submissionFilter, fetchSubmissions]);

    return (
        <PageableGrid
            rowsData={rowsData}
            totalRowsCount={totalCount}
            columns={columns}
            catalogLoading={documentTypeCatalogLoading}
            onFetchMore={handleFetchMore}
            selectedRowId={selectedSubmissionId}
            onRowSelect={onSelectSubmission}
            onRowDoubleClick={onSubmissionDoubleClick}
        />
    );
}
